import { Translation } from '@ps/web-shared-components';
import { setCustomUrl } from '@snowplow/browser-tracker';
import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useLocation } from 'react-router';
import { CSSTransition } from 'react-transition-group';

import useModal from '@swf/core/hooks/useModal';
import useTrackers from '@swf/core/hooks/useTrackers';
import useTrackingLabel from '@swf/core/hooks/useTrackingLabel';
import type { TrackedTranslation, TranslationTag } from '@swf/core/types/i18n';

import Layout from '../Container/Layout';

import styles from './Modal.scss';

export interface Props {
  visible: boolean;
  onMount?(): void;
  onClose: React.MouseEventHandler;
  children?: ReactNode;
  title?: TrackedTranslation | TranslationTag;
}

const Modal: React.FC<Props> = ({ title, onMount, onClose, visible, children }) => {
  const [panelVisible, setPanelVisible] = useState(false);
  const [animate, setAnimate] = useState(false);
  const { addOnModalClose, removeOnModalClose } = useModal();
  const { pageView } = useTrackers();

  const { pathname } = useLocation();
  const snowplowRef = useRef<string>('');
  const modalTitle = useTrackingLabel(title as TrackedTranslation);
  const initialPathnameRef = useRef<string>(pathname);
  const modalPathname =
    initialPathnameRef.current +
    `/modal/${modalTitle && modalTitle.toLowerCase().replace(/ /g, '-')}`;

  const handleEntered = () => {
    onMount?.();
  };

  const handleExiting = () => {
    setCustomUrl(pathname);
    snowplowRef.current = '';
  };

  const handleExited = () => {
    setAnimate(false);
    setPanelVisible(false);
    removeOnModalClose();
  };

  useEffect(() => {
    if (visible && !panelVisible) {
      setPanelVisible(visible);
      addOnModalClose(onClose);
      window.setTimeout(() => setAnimate(true));
    }
    if (!visible && panelVisible) {
      setAnimate(false);
    }
  }, [visible, panelVisible, addOnModalClose, onClose]);

  useEffect(() => {
    if (
      panelVisible &&
      snowplowRef.current !== modalPathname &&
      pathname === initialPathnameRef.current
    ) {
      snowplowRef.current = modalPathname;
      pageView(modalPathname);
    }
  }, [modalPathname, pageView, pathname, panelVisible]);

  return (
    <ReactModal
      isOpen={panelVisible}
      overlayClassName={styles.overlay}
      className={styles.modal}
      bodyOpenClassName={styles.bodyOpen}
    >
      <CSSTransition
        classNames={{
          enter: styles.enter,
          enterActive: styles.enterActive,
          exit: styles.exit,
          exitActive: styles.exitActive,
          exitDone: styles.exitDone
        }}
        in={animate}
        timeout={500}
        onEntered={handleEntered}
        onExiting={handleExiting}
        onExited={handleExited}
        mountOnEnter
      >
        <Layout
          headerProps={{
            title: <Translation {...(title as TrackedTranslation)} />,
            buttons: {
              onCloseClick: onClose
            },
            modal: true
          }}
        >
          {children}
        </Layout>
      </CSSTransition>
    </ReactModal>
  );
};
export default Modal;
